import * as React from 'react';
import { useCallback, useEffect } from 'react';

import TranscriptHighlightButton from 'bundles/interactive-transcript/components/TranscriptHighlightButton';
import { getModifierKeyForPlatform } from 'bundles/interactive-transcript/utils/HotkeyUtils';
import type { Highlight } from 'bundles/video-highlighting/types';

import _t from 'i18n!nls/interactive-transcript';

type Props = {
  targetedHighlight: Highlight;
  onRemove: () => void;
  showKeyboardControls: boolean;
};

const RemoveTranscriptHighlight = ({ targetedHighlight, onRemove, showKeyboardControls }: Props) => {
  const modiferKey = getModifierKeyForPlatform();
  const keyboardControlLabel = _t('Press [#{modiferKey} + D] to Delete Note', { modiferKey });
  const label = showKeyboardControls ? keyboardControlLabel : _t('Delete Note');

  const handleDeleteButtonMouseDown = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    () => (event: MouseEvent) => {
      const isDeleteNoteButtonClicked = event.target && (event.target as HTMLElement).innerText === label;

      if (isDeleteNoteButtonClicked) {
        onRemove();
      }
    },
    [label, onRemove]
  );

  /**
   * The updateSelection function in the TranscriptHighlighter Component is triggered
   * by the mouseup event which swallows the click event on the remove button.
   * Adding the mousedown event ensures onRemove is triggered when the Delete Note button is clicked.
   */
  useEffect(() => {
    document.addEventListener('mousedown', handleDeleteButtonMouseDown());

    return () => {
      document.removeEventListener('mousedown', handleDeleteButtonMouseDown());
    };
  }, [handleDeleteButtonMouseDown]);

  if (!targetedHighlight.transcriptTextStartIndex) {
    return null;
  }

  const { cueIndex } = targetedHighlight.transcriptTextStartIndex;
  return (
    <TranscriptHighlightButton
      onClick={onRemove}
      cueIndex={cueIndex}
      label={label}
      trackingName="delete_transcript_highlight"
    />
  );
};

export default RemoveTranscriptHighlight;
